/**
 * Truncates the given text up to the given length with a trailing ellipsis.
 *
 * @param text The text that will be truncated up to the given length.
 * @param length The maximum length of the text before it gets truncated.
 * @returns The truncated text, if it exceeds the given length, or the original
 *   text.
 */
export function truncateText(text: string, length: number): string {
  return text.length > length ? `${text.slice(0, length)}...` : text;
}

/**
 * Capitalizes the first letter in the given text.
 *
 * @param text The text that will have its first letter capitalized.
 */
export function capitalizeFirstLetter(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/**
 * Formats the given range into dollar amounts.
 *
 * @param range The range to be formatted into dollar amounts.
 * @returns A string representation of the formatted range.
 */
export function formatRangeToDollars(range: { gte?: number; lte?: number }): string {
  return range.gte && range.lte
    ? `${formatAmountToDollars(range.gte)} - ${formatAmountToDollars(range.lte)}`
    : range.gte
      ? `> ${formatAmountToDollars(range.gte)}`
      : range.lte
        ? `< ${formatAmountToDollars(range.lte)}`
        : '';
}

/**
 * Formats the given amount into its equivalent a dollar value.
 *
 * @param amount The amount to be formatted into a dollar value.
 * @returns A string representation of the formatted amount.
 */
export function formatAmountToDollars(amount: number): string {
  const options: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: 'USD',
    notation: 'compact',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    trailingZeroDisplay: 'stripIfInteger',
    roundingMode: 'floor',
  };

  return new Intl.NumberFormat('en-US', options).format(amount);
}
